import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TableFilter } from '../metadata/propertyConfig';
import { SelectOption } from '../../../../types/common';
import {
  getFilterChain,
  getPropertyStatusSelectOptions,
  getSortByString,
  parseSortByString
} from '../utils/propertyListUtils';
import type { SortState } from '@/types/table';
import { DateRangeValue } from '../date-range-filter/DateRangeFilter';
import { getEntityOptions, isChildEntityScoped } from '../../../../services/entity-tree';
import { getEntitySubEntitiesDropdown } from '../../../../services/current-context';
import { PERSISTENCE_KEYS } from '../../../../util/persistedState';
import { useGetPropertyStatusesQuery } from '../../services/propertyApi';
import {
  DEFAULT_LISTING_FILTERS,
  FILTER_BASE,
  ListingFilters,
  useListingFilters
} from '../useListingFilters';
import { hasActiveHomeFilters } from '../utils/advancedFilterUtils';
import { resolveOrderView } from '../../../orders/order-views/orderViews';
import { useEntityTree, useSelectedEntityId } from '../../../../hooks/useEntityScope';

export { FILTER_BASE };

// `id` is passed in rather than read from localStorage: the scope lives on the
// slice now, and this is a plain function that cannot call a hook (DV-3013).
function getChildEntity(opts: { value: string; label: string }[], id: string) {
  return opts.find((o) => o.value === id);
}

export function usePropertyFilters(
  isProperty: boolean,
  onPageChange: (u: number, r: number, f?: string, s?: string, q?: string, e?: string) => void,
  page: number[],
  sort: string
) {
  const [queryParameters] = useSearchParams();

  // The scope source, subscribed rather than read at mount: a switch stops
  // reloading the document in PR 3, and a cached read would leave this page
  // filtering the previous account (DV-3013 spec §2.1 class A / §2.2).
  const selectedEntityId = useSelectedEntityId() ?? '';
  const entityTree = useEntityTree();

  // Preset views (?view=) lock the Order Status filter for orders. The locked
  // set is applied to the fetched chain only — the persisted orderStatusValues
  // / filter.orderStatus remain the user's own All-Orders selection.
  // For properties, isProperty=true short-circuits to null: no status locking.
  const orderView = isProperty ? null : resolveOrderView(queryParameters.get('view'));
  const lockedStatuses = orderView?.lockedStatuses ?? null;
  const withEffectiveStatus = (f: TableFilter): TableFilter =>
    lockedStatuses ? { ...f, orderStatus: lockedStatuses } : f;

  // DV-3122: the sort a request carries when its caller passes none, and
  // what the table header shows (PropertyListPage passes it to DataTable).
  // An explicit sort (`sort`, owned by usePropertyListData and written only
  // by a header, mobile-menu or action-bar sort) wins; then the view's
  // default; then '' and the backend applies its own (updatedAt desc).
  // Properties have no view, so they only ever see an explicit sort.
  const effectiveSortState: SortState | null =
    parseSortByString(sort) ?? orderView?.defaultSort ?? null;
  const effectiveSort = effectiveSortState
    ? getSortByString(effectiveSortState.selector, effectiveSortState.asc)
    : '';

  const storageKey = isProperty
    ? PERSISTENCE_KEYS.propertiesFilters
    : PERSISTENCE_KEYS.ordersFilters;

  // Captured at first render — before useListingFilters' useEffect writes the
  // initial bundle. getInitialFilterChain reads this to decide whether to
  // restore the persisted bundle or apply route defaults.
  const wasPersistedRef = useRef<boolean>(
    sessionStorage.getItem(storageKey) !== null
  );

  // Route-default seed for the persistence bundle. useListingFilters captures
  // this once at mount; isProperty is route-bound so the deps array is empty.
  const initialBundle = useMemo<ListingFilters>(() => {
    const childEntity = isChildEntityScoped()
      ? getChildEntity(getEntityOptions(), selectedEntityId)
      : undefined;
    return {
      ...DEFAULT_LISTING_FILTERS,
      selectedEntityFilters: childEntity ? [childEntity] : [],
      activeStatus: isProperty
        ? { value: 'Active Units', label: 'Active Units' }
        : null
    };
    // Captured once at mount per the comment above; isProperty is route-bound
    // and stable for the page's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { filters, setFilters } = useListingFilters(storageKey, initialBundle);

  const filter = filters.filter;
  const moreFilters = filters.moreFilters;
  const filtersCount = filters.filtersCount;
  const orderDateRange = filters.orderDateRange;
  const orderStatusValues = filters.orderStatusValues;
  const selectedEntityFilters = filters.selectedEntityFilters;
  const activeStatus = filters.activeStatus;

  const setFilter = (ft: TableFilter) =>
    setFilters((prev) => ({ ...prev, filter: ft }));
  const setFiltersCount = (n: number) =>
    setFilters((prev) => ({ ...prev, filtersCount: n }));
  const setOrderDateRange = (range: DateRangeValue | null) =>
    setFilters((prev) => ({ ...prev, orderDateRange: range }));
  const setOrderStatusValues = (values: { value: string; label: string }[]) =>
    setFilters((prev) => ({ ...prev, orderStatusValues: values }));
  const setSelectedEntityFilters = (
    values: { value: string; label: string }[]
  ) => setFilters((prev) => ({ ...prev, selectedEntityFilters: values }));

  const [showClearFilterBtn, setShowClearFilterBtn] = useState(false);
  const [openMoreFilters, setOpenMoreFilters] = useState(false);
  const [propertyStatusOptions, setPropertyStatusOptions] = useState<SelectOption[]>([]);
  const [entityFilterOptions, setEntityFilterOptions] = useState(() => getEntityOptions());

  const propertyStatusRef = useRef<HTMLInputElement | undefined>();
  const addressRef = useRef<HTMLInputElement | undefined>();
  const activeStatusRef = useRef<unknown>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Subscribes to the tree on the slice, replacing the `entityTreeUpdated`
  // window event — a hand-rolled subscription to a localStorage write
  // (DV-3013 spec §2.6).
  //
  // Unlike that listener, this also runs on mount, where `initialBundle` has
  // already seeded the same pin from the same two functions. The value is
  // identical, but setting it again would build a fresh array into a fresh
  // `filters` object and re-run anything keyed on `filters` — so the pin is
  // only written when it actually differs (PR #1146 review, finding 4).
  useEffect(() => {
    const next = getEntityOptions();
    setEntityFilterOptions(next);
    if (isChildEntityScoped()) {
      const m = getChildEntity(next, selectedEntityId);
      const alreadyPinned =
        selectedEntityFilters.length === 1 && selectedEntityFilters[0]?.value === m?.value;
      if (m && !alreadyPinned) setSelectedEntityFilters([m]);
    }
    // setSelectedEntityFilters reads from closure via setFilters((prev) => ...)
    // so latest state is used.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityTree, selectedEntityId]);

  const { data: propertyStatuses } = useGetPropertyStatusesQuery(undefined, {
    skip: !isProperty,
  });
  useEffect(() => {
    if (propertyStatuses) {
      setPropertyStatusOptions(getPropertyStatusSelectOptions(propertyStatuses));
    }
  }, [propertyStatuses]);

  // `mf` overrides the closure-captured moreFilters when a caller is updating
  // it in the same call (apply/reset/clear) — closures see the stale value
  // until the setFilters commit re-renders.
  const checkFilterObject = (obj: TableFilter, mf: string = moreFilters) => {
    setShowClearFilterBtn(hasActiveHomeFilters(obj, isProperty) || mf !== '');
  };

  const appendFilter = (
    f: TableFilter, p: number, s: number, ss = '', mf = ''
  ) => {
    const entity = getEntitySubEntitiesDropdown();
    onPageChange(p, s, getFilterChain(withEffectiveStatus(f), mf || moreFilters, isProperty), ss || effectiveSort, '', entity[0]?.value);
  };

  // Refetch when the preset view changes (e.g., sidebar nav switches from
  // In Progress → Completed). The component stays mounted; only the query
  // string changes — so we must re-apply the chain manually.
  const viewKey = orderView?.key ?? null;
  const viewMountedRef = useRef(false);
  useEffect(() => {
    if (isProperty) return;
    // Skip the mount run — the initial fetch is seeded by getInitialFilterChain.
    if (!viewMountedRef.current) {
      viewMountedRef.current = true;
      return;
    }
    appendFilter(filter, 1, page[1] ?? 25, sort, moreFilters);
    // Refetch on view switch only; appendFilter/filter are recreated per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewKey]);

  const handleFilterChange = (key: string, value: string) => {
    const ft = { ...filter };
    if (key === 'propertyAddress') {
      ft.address = value;
      setFilter(ft);
      checkFilterObject(ft);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => appendFilter(ft, 1, 25, sort, moreFilters), 500);
      return;
    }
    if (key === 'propertyStatus') ft.status = value || null;
    setFilter(ft);
    checkFilterObject(ft);
    appendFilter(ft, 1, 25, sort, moreFilters);
  };

  const clearFilter = (currentSort: string) => {
    const c: TableFilter = {
      ...filter,
      status: null,
      orderStatus: null,
      orderDateFrom: null,
      orderDateTo: null,
      activeStatus: isProperty ? 'Active Units' : null
    };
    let nextEntities: { value: string; label: string }[] = [];
    if (isChildEntityScoped()) {
      const m = getChildEntity(entityFilterOptions, selectedEntityId);
      nextEntities = m ? [m] : [];
      c.entityUUIDs = nextEntities.length > 0 ? nextEntities.map((e) => e.value) : null;
    } else {
      c.entityUUIDs = null;
    }
    queryParameters.delete('orderId');
    sessionStorage.removeItem('orderId');

    setFilters((prev) => ({
      ...prev,
      filter: c,
      selectedEntityFilters: nextEntities,
      orderDateRange: null,
      orderStatusValues: [],
      activeStatus: isProperty
        ? { value: 'Active Units', label: 'Active Units' }
        : null,
      moreFilters: '',
      filtersCount: 0
    }));

    checkFilterObject(c, '');
    appendFilter(c, 1, 25, currentSort, 'dummyFilter=dummyFilter');
  };

  // `quickFilterCommit` (Task 14) lets the mobile filter sheet's Apply merge
  // its staged quick-filter values (Order Status/Date/Entity, or property
  // Unit Status) into the SAME setFilters + appendFilter call as the advanced
  // `mf` commit below. This exists because `filter`/`moreFilters` here are
  // this-render closures: two separate onApplyFilters-style calls in the same
  // click (one for advanced, one for quick) would each read the OTHER's
  // pre-update snapshot, so whichever fired last would silently discard the
  // other's change from the network request even though the batched React
  // state itself would (via functional updates) end up correct. One call,
  // one merge, one appendFilter avoids that race outright.
  const onApplyFilters = (
    mf: string,
    activeStatusValue: { value: string; label: string } | null,
    quickFilterCommit?: {
      orderStatusValues?: { value: string; label: string }[];
      orderDateRange?: DateRangeValue | null;
      selectedEntityFilters?: { value: string; label: string }[];
      propertyStatus?: string | null;
    }
  ) => {
    queryParameters.delete('orderId');
    sessionStorage.removeItem('orderId');
    // For orders the drawer doesn't manage activeStatus — keep current value.
    const nextActive = isProperty ? activeStatusValue : activeStatus ?? null;
    const nextFilter: TableFilter = {
      ...filter,
      activeStatus: nextActive?.value ?? null,
      ...(quickFilterCommit?.propertyStatus !== undefined
        ? { status: quickFilterCommit.propertyStatus }
        : {}),
      ...(quickFilterCommit?.orderStatusValues !== undefined
        ? {
            orderStatus: quickFilterCommit.orderStatusValues.length > 0
              ? quickFilterCommit.orderStatusValues.map((o) => o.value)
              : null
          }
        : {}),
      ...(quickFilterCommit?.orderDateRange !== undefined
        ? {
            orderDateFrom: quickFilterCommit.orderDateRange?.from ?? null,
            orderDateTo: quickFilterCommit.orderDateRange?.to ?? null
          }
        : {}),
      ...(quickFilterCommit?.selectedEntityFilters !== undefined
        ? {
            entityUUIDs: quickFilterCommit.selectedEntityFilters.length > 0
              ? quickFilterCommit.selectedEntityFilters.map((o) => o.value)
              : null
          }
        : {})
    };
    setFilters((prev) => ({
      ...prev,
      moreFilters: mf,
      activeStatus: nextActive,
      filter: nextFilter,
      ...(quickFilterCommit?.orderStatusValues !== undefined
        ? { orderStatusValues: quickFilterCommit.orderStatusValues }
        : {}),
      ...(quickFilterCommit?.orderDateRange !== undefined
        ? { orderDateRange: quickFilterCommit.orderDateRange }
        : {}),
      ...(quickFilterCommit?.selectedEntityFilters !== undefined
        ? { selectedEntityFilters: quickFilterCommit.selectedEntityFilters }
        : {})
    }));
    checkFilterObject(nextFilter, mf);
    if (mf.length > 0) appendFilter(nextFilter, 0, page[1], sort, mf);
    else appendFilter(nextFilter, page[0], page[1], sort, 'dummyFilter=dummyFilter');
  };

  const onResetFilters = () => {
    queryParameters.delete('orderId');
    sessionStorage.removeItem('orderId');
    // Drawer reset restores all drawer-managed filters to defaults.
    // For property that includes activeStatus → Active Units.
    const defaultActive = isProperty
      ? { value: 'Active Units', label: 'Active Units' }
      : (activeStatus ?? null);
    const nextFilter: TableFilter = {
      ...filter,
      activeStatus: isProperty ? 'Active Units' : filter.activeStatus ?? null
    };
    setFilters((prev) => ({
      ...prev,
      moreFilters: '',
      activeStatus: defaultActive,
      filter: nextFilter
    }));
    checkFilterObject(nextFilter, '');
    appendFilter(nextFilter, page[0], page[1], sort, 'dummyFilter=dummyFilter');
  };

  const hideModal = () => setOpenMoreFilters((p) => !p);

  // Order ID, property ID, and address all flow through the main search input
  // and the order-service's `address` query param (which matches orderNumber +
  // clientPropertyId server-side). Two deep-link signals can pre-populate it:
  //   - Fresh URL signal (`?address=`, `?orderId=`) overrides persisted state.
  //   - sessionStorage('orderId') fallback is only honored on a fresh visit;
  //     a stale session value cannot clobber a typed search after refresh.
  const getDeepLinkAddress = (): string => {
    if (isProperty) return '';
    return queryParameters.get('address')
      || queryParameters.get('orderId')
      || queryParameters.get('propertyAddress')
      || '';
  };

  const getStashedOrderId = (): string => {
    if (isProperty) return '';
    return sessionStorage?.getItem('orderId') || '';
  };

  const consumeDeepLinkUrl = () => {
    const clean = window.location.hash.split('?')[0];
    window.history.replaceState(null, '', `${window.location.pathname}${clean}`);
  };

  const getInitialFilterChain = () => {
    const entity = getEntitySubEntitiesDropdown();
    const freshUrlSignal = getDeepLinkAddress();
    const stashedOrderId = getStashedOrderId();

    // Fresh URL signal (?orderId=, ?address=, ?propertyAddress=) acts as a
    // fresh entry point: reset every persisted filter slice so the deep-link
    // target isn't hidden behind a stale filter and chips/badge stay in sync
    // with the underlying filter object. Child-entity scoping is retained —
    // those users can't fully unscope. Only reachable on orders since
    // getDeepLinkAddress short-circuits on isProperty.
    if (freshUrlSignal) {
      consumeDeepLinkUrl();
      const childEntity = isChildEntityScoped()
        ? getChildEntity(entityFilterOptions, selectedEntityId)
        : undefined;
      const nextEntities = childEntity ? [childEntity] : [];
      const cleanBase: TableFilter = {
        ...FILTER_BASE,
        entityUUIDs: nextEntities.length > 0 ? nextEntities.map((e) => e.value) : null,
        address: freshUrlSignal
      };
      setFilters((prev) => ({
        ...prev,
        filter: cleanBase,
        selectedEntityFilters: nextEntities,
        orderDateRange: null,
        orderStatusValues: [],
        activeStatus: null,
        moreFilters: '',
        filtersCount: 0
      }));
      // Only `address` is set on cleanBase; checkFilterObject excludes it
      // (and moreFilters is now empty), so Clear Filters stays hidden — the
      // deep-link search is cleared via the input's X.
      setShowClearFilterBtn(false);
      // Deep-link entry (?orderId=/?address=) wins over any ?view= lock so the
      // targeted order can't be filtered out of view.
      // The sort still follows the view: it cannot hide the target, and it
      // must match the header and the pages that follow.
      return {
        filterStr: getFilterChain(cleanBase, '', isProperty),
        entityValue: entity[0]?.value || '',
        queryParamData: '',
        sortStr: effectiveSort,
        // A fresh entry to a targeted order: the list opens on page 1 rather
        // than wherever the user last left it (DV-3455).
        isDeepLink: true
      };
    }

    // Persisted state takes precedence on revisit. Stashed orderId is *not*
    // an override and is only honored on a fresh visit (below).
    if (wasPersistedRef.current) {
      checkFilterObject(filter);
      return {
        filterStr: getFilterChain(withEffectiveStatus(filter), moreFilters, isProperty),
        entityValue: entity[0]?.value || '',
        queryParamData: '',
        sortStr: effectiveSort,
        isDeepLink: false
      };
    }

    const euuids = isChildEntityScoped() && selectedEntityFilters.length > 0
      ? selectedEntityFilters.map((e) => e.value) : null;
    const init: TableFilter = isProperty
      ? { ...FILTER_BASE, activeStatus: 'Active Units', entityUUIDs: euuids }
      : { ...FILTER_BASE, entityUUIDs: euuids, ...(stashedOrderId ? { address: stashedOrderId } : {}) };
    setFilter(init);
    if (isProperty) checkFilterObject(init);
    return {
      filterStr: getFilterChain(withEffectiveStatus(init), '', isProperty),
      entityValue: entity[0]?.value || '',
      queryParamData: '',
      sortStr: effectiveSort,
      isDeepLink: false
    };
  };

  return {
    filter, moreFilters, filtersCount, setFiltersCount,
    showClearFilterBtn,
    orderDateRange, setOrderDateRange, orderStatusValues, setOrderStatusValues,
    activeStatus, propertyStatusOptions, entityFilterOptions,
    selectedEntityFilters, setSelectedEntityFilters,
    propertyStatusRef, addressRef, activeStatusRef,
    handleFilterChange, checkFilterObject, appendFilter, clearFilter,
    onApplyFilters, onResetFilters, openMoreFilters, hideModal,
    getInitialFilterChain, setFilter, effectiveSortState
  };
}
